import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, useHref } from 'react-router-dom';
import { Button, Input, Card, Alert } from '@dreamtree-org/twreact-ui';
import { Store, ArrowLeft } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { setAuth } from '@rbac/permissions';
import { useStoreLogo } from '@/lib/storeLogo';
import { DemoAccounts, useTapReveal, fetchDemoAccounts } from '@dreamtree-org/landing-kit/auth';
import '@dreamtree-org/landing-kit/auth.css';
import { getSsoConfig, fetchSsoSession, ssoErrorMessage, ssoStartUrl } from '@/lib/dreamtreeSso';

/**
 * Merchant back-office sign-in, branded with the store resolved from the host.
 * Three quick taps on "Sign in" reveal the demo accounts (D40; the server answers 404
 * unless DEMO_ACCOUNTS=1, and then nothing shows). A chip fills the form, never submits.
 */
export default function Login({ store }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { fetchApi, loading } = useAppApi();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const storeName = (store && store.name) || 'MerchantPro';
  const logo = useStoreLogo();
  const demo = useTapReveal({ taps: 3 });
  /**
   * Dreamtree ID (DTID-44). `sso` is null unless the server reports hybrid mode
   * (`/api/meta/context` carries it), so in local mode this page is exactly the
   * password form. `?sso=1` is the callback coming back with a fresh session
   * cookie; `?sso_error=<code>` is a refused callback, mapped to our own text.
   */
  const sso = getSsoConfig();
  const [params] = useSearchParams();
  const loginPath = useHref('/login');
  const from = (loc.state && loc.state.from) || params.get('from') || null;
  const ssoError = ssoErrorMessage(params.get('sso_error'));
  const [adopting, setAdopting] = useState(Boolean(sso) && params.get('sso') === '1');

  useEffect(() => {
    if (!sso || params.get('sso') !== '1') return undefined;
    let live = true;
    fetchSsoSession()
      .then((user) => {
        if (!live) return;
        setAuth(user);
        nav(from || '/', { replace: true });
      })
      .catch((ex) => {
        if (!live) return;
        setAdopting(false);
        setErr(ex.message || 'Sign-in with Dreamtree failed');
      });
    return () => {
      live = false;
    };
    // Once per arrival from the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continueWithDreamtree = () => {
    window.location.assign(ssoStartUrl({ startPath: sso.startPath, loginPath, from }));
  };

  const fillDemo = (a) => {
    setEmail(a.identifier);
    setPassword(a.password);
    setErr('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const res = await fetchApi('login', { email, password });
      setAuth((res && res.data) || res);
      nav((loc.state && loc.state.from) || '/', { replace: true });
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-4 pt-safe pb-safe bg-[var(--app-bg)]">
      <Card className="w-full max-w-sm p-6 space-y-5">
        <div className="text-center space-y-2">
          {logo ? (
            <img src={logo.url} alt={logo.alt || storeName} className="mx-auto h-12 w-auto max-w-[10rem] object-contain" />
          ) : (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950">
              <Store size={24} aria-hidden />
            </div>
          )}
          <h1 className="text-xl font-bold text-primary-600">{storeName}</h1>
          <h2 className="text-base font-semibold cursor-default" {...demo.tapProps}>Sign in</h2>
          <p className="text-sm font-medium">Store administration</p>
          <p className="text-xs opacity-70">Sign in to manage inventory, listings, orders and payouts.</p>
        </div>
        {err || ssoError ? <Alert variant="error" title="Sign-in failed">{err || ssoError}</Alert> : null}
        <form onSubmit={submit} className="space-y-4">
          <Input label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <Button type="submit" fullWidth loading={loading}>Sign in</Button>
        </form>
        {sso ? (
          <div className="space-y-3">
            <p className="text-center text-xs opacity-70">or</p>
            <Button type="button" variant="outline" fullWidth loading={adopting} onClick={continueWithDreamtree}>
              Continue with Dreamtree
            </Button>
          </div>
        ) : null}
        {demo.revealed ? <DemoAccounts className="mp-auth-demo" load={fetchDemoAccounts} onSelect={fillDemo} /> : null}
      </Card>
      {/* The storefront lives outside the /admin basename — a full page load. */}
      <a href="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 min-h-[44px]">
        <ArrowLeft size={14} aria-hidden /> Back to the {storeName} storefront
      </a>
    </div>
  );
}
